import { useEffect, useState } from 'react'

import { type Learner } from '@/platform/learners/api'
import { type MaterialActivity } from '@/platform/materials/learning'

import { HELP_LABELS, type HelpMode } from '../materials/model'
import {
  independentPracticeEvidence,
  observedLine,
  patternLine,
  summarizeRun,
} from '../materials/report'
import { PARENT_NO_MASTERY_TEXT, STATUS_TEXT } from '../materials/ui'

/**
 * Parent Learning Insight from persisted evidence (D-14.4, D-17). Everything
 * shown comes from `get_learner_material_activity` for learners of this
 * family: the source the learner confirmed, the exercises, each answer, its
 * correctness and the help opened before answering. "Quan sát" are facts;
 * "Nhận định" is the deterministic rule set of report.ts (assisted answers
 * not counted, one wrong answer is not a pattern, few answers are
 * insufficient evidence) — not AI, and never a mastery score or percentage.
 */

export type LoadMaterialActivity = (learnerId: string) => Promise<MaterialActivity[]>

type State =
  | { kind: 'loading' }
  | { kind: 'error' }
  | {
      kind: 'ready'
      byLearner: readonly (readonly [Learner, readonly MaterialActivity[]])[]
    }

const PROVIDER_TEXT: Readonly<Record<string, string>> = {
  'google-document-ai': 'đọc bằng Google Document AI',
  'test-fixture': 'ĐỌC THỬ bằng TEST FIXTURE (máy phát triển)',
}

function date(iso: string): string {
  return new Date(iso).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })
}

export function MaterialActivityView({
  learners,
  load,
  onLoaded,
}: {
  learners: readonly Learner[]
  load: LoadMaterialActivity
  /** Reports whether any real evidence exists (to keep DEMO previews apart). */
  onLoaded?: (hasEvidence: boolean) => void
}): React.JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' })
  const [reload, setReload] = useState(0)

  useEffect(() => {
    let active = true
    Promise.all(learners.map(async (l) => [l, await load(l.id)] as const)).then(
      (byLearner) => {
        if (!active) return
        setState({ kind: 'ready', byLearner })
        onLoaded?.(byLearner.some(([, a]) => a.length > 0))
      },
      () => {
        if (active) setState({ kind: 'error' })
      },
    )
    return () => {
      active = false
    }
  }, [learners, load, reload, onLoaded])

  if (state.kind === 'loading') return <p role="status">Đang tải tình hình học tập…</p>
  if (state.kind === 'error')
    return (
      <div className="flex flex-col items-start gap-2">
        <p role="alert">Chưa tải được tình hình học tập.</p>
        <button
          type="button"
          className="h-11 rounded-xl border border-slate-300 px-4 dark:border-slate-700"
          onClick={() => {
            setState({ kind: 'loading' })
            setReload((r) => r + 1)
          }}
        >
          Thử lại
        </button>
      </div>
    )

  const any = state.byLearner.some(([, a]) => a.length > 0)
  if (!any)
    return (
      <p className="text-slate-700 dark:text-slate-300">
        Chưa có báo cáo học tập: các bé chưa học bài nào từ tài liệu ở trường.
      </p>
    )

  return (
    <div className="flex flex-col gap-4">
      {state.byLearner
        .filter(([, a]) => a.length > 0)
        .map(([learner, activities]) => (
          <section
            key={learner.id}
            aria-label={`Học từ tài liệu: ${learner.nickname}`}
            className="flex flex-col gap-3"
          >
            <h3 className="text-lg font-semibold">{learner.nickname}</h3>
            {activities.map((a) => (
              <ActivityCard key={a.set_id} activity={a} />
            ))}
          </section>
        ))}
    </div>
  )
}

function ActivityCard({
  activity: a,
}: {
  activity: MaterialActivity
}): React.JSX.Element {
  // D-21 §1.5: only answers to independent practice are evidence.
  const evidence = independentPracticeEvidence(a.items, a.attempts)
  const topic = evidence.topic
  const summary = summarizeRun(topic, evidence.attempts)
  const lines = a.source.blocks.filter((b) => b.text !== null).map((b) => b.text ?? '')
  return (
    <article className="flex flex-col gap-3 rounded-2xl border border-slate-200 p-4 dark:border-slate-800">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-lg font-semibold">{a.topic_label}</p>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {date(a.created_at)} · {a.completed_at === null ? 'đang làm dở' : 'đã làm xong'}
        </p>
      </header>

      <div className="text-sm text-slate-700 dark:text-slate-300">
        <p>
          <span className="font-semibold">Nguồn: </span>
          Tài liệu Toán bé gửi, {a.source.page_count} trang,{' '}
          {PROVIDER_TEXT[a.source.provider_id] ?? a.source.provider_id}; bé đã kiểm tra và
          xác nhận nội dung.
        </p>
        {a.source.text_deleted ? (
          <p>Nội dung tài liệu đã được xóa; kết quả luyện tập vẫn được giữ.</p>
        ) : (
          <p className="font-serif">{lines.join(' · ')}</p>
        )}
        <p>
          Giải thích:{' '}
          {a.explanation_origin === 'ai' ? 'do AI viết' : 'soạn sẵn (không phải AI)'} ·
          Bài luyện: tạo theo quy tắc cùng chủ đề, với số khác bài trong tài liệu; chấm tự
          động. Ứng dụng không giải bài trong tài liệu của bé.
        </p>
      </div>

      <section aria-label="Quan sát" className="flex flex-col gap-1">
        <p className="text-sm font-semibold uppercase text-slate-600 dark:text-slate-400">
          Quan sát
        </p>
        <p>{observedLine(summary)}</p>
        {evidence.excludedSourceAnswers > 0 && (
          <p className="text-sm text-slate-700 dark:text-slate-300">
            {evidence.excludedSourceAnswers} câu là bài trong tài liệu của bé: không tính
            là bằng chứng học tập.
          </p>
        )}
        <ul className="flex flex-col gap-1 text-base">
          {a.items.map((item, i) => {
            const x = a.attempts.find((t) => t.item_id === item.item_id)
            return (
              <li key={item.item_id}>
                Câu {i + 1} (
                {item.origin === 'from_source'
                  ? 'bài trong tài liệu, không tính là bằng chứng'
                  : 'luyện độc lập'}
                ): {item.prompt.replace('Tính: ', '')} —{' '}
                {x === undefined
                  ? 'chưa làm'
                  : `${x.is_correct ? '✓ đúng' : `✗ trả lời ${x.answer}${x.correct_display === '' ? '' : `, đáp án ${x.correct_display}`}`}${
                      x.help_used.length > 0
                        ? ` · có trợ giúp: ${x.help_used.map((m) => HELP_LABELS[m as HelpMode]).join(', ')}`
                        : ' · tự làm'
                    }`}
              </li>
            )
          })}
        </ul>
      </section>

      <section
        aria-label="Nhận định"
        className="flex flex-col gap-1 rounded-xl bg-slate-50 p-3 dark:bg-slate-900"
      >
        <p className="text-sm font-semibold uppercase text-slate-600 dark:text-slate-400">
          Nhận định (theo quy tắc cố định, không phải AI)
        </p>
        <p className="font-semibold">{STATUS_TEXT[summary.status]}</p>
        {summary.patterns.map((p) => (
          <p key={p.tag}>{patternLine(topic, p)}</p>
        ))}
        <p>
          <span className="font-semibold">Việc nên làm tiếp: </span>
          {summary.nextAction.text}{' '}
          <span className="text-slate-600 dark:text-slate-400">
            ({summary.nextAction.reason})
          </span>
        </p>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {PARENT_NO_MASTERY_TEXT}
        </p>
      </section>
    </article>
  )
}
